import { AuthenticatedRoutesGuard, getOrgSlugFromUrl, AuthProvider as SharedAuthProvider } from '@tf/shared';
import type React from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { ApplicationLayout } from '@/components/application';
import { AppLayout } from '@/components/shared';
import { I18nProvider, useConfig } from '@/core/providers';
import { AppMetaProvider } from '@/core/providers/AppMetaProvider';
import { EndClientAreaBackendClientInitialization } from '@/core/providers/BackendClientInitialization';
import { DefinitionsProvider } from '@/core/providers/DefinitionsProvider';
import { OriginTenantSettingsProvider, useOriginTenantSettings } from '@/core/providers/OriginTenantSettingsProvider';
import { ApplicationConnectionRoute } from '@/routes/endClientArea/ApplicationConnectionRoute';
import { ApplicationFormRoute } from '@/routes/endClientArea/ApplicationFormRoute';
import { ApplicationRoute } from '@/routes/endClientArea/ApplicationRoute';
import { ApplicationSubmitRoute } from '@/routes/endClientArea/ApplicationSubmitRoute';
import { MainRoute } from '@/routes/endClientArea/MainRoute';
import { endClientAreaPaths } from '@/routes/endClientArea/paths';
import { SignInRoute } from '@/routes/endClientArea/SignInRoute';
import { SignOutRoute } from '@/routes/endClientArea/SignOutRoute';
import { SuccessRoute } from '@/routes/endClientArea/SuccessRoute';
import { NotFoundRoute } from '@/routes/NotFoundRoute';

const AuthProvider: React.FC = () => {
	const config = useConfig();
	return (
		<SharedAuthProvider publishableKey={config.auth.clerk_publishable_key}>
			<Outlet />
		</SharedAuthProvider>
	);
};

const AuthZoneI18nProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const originTenantSettings = useOriginTenantSettings();
	return <I18nProvider defaultLocale={originTenantSettings.settings.default_locale}>{children}</I18nProvider>;
};

const AuthZoneGuardAndProviders: React.FC = () => {
	const requiredOrgSlug = getOrgSlugFromUrl();

	return (
		<AuthenticatedRoutesGuard
			signInPagePath={endClientAreaPaths.signIn()}
			checkOrgMembershipFn={(membership) => {
				const isRequiredOrgActive = requiredOrgSlug === membership.organization.slug?.toLowerCase();
				return isRequiredOrgActive && membership.role === 'admin';
			}}
		>
			<AppMetaProvider>
				<OriginTenantSettingsProvider>
					<AuthZoneI18nProvider>
						<DefinitionsProvider>
							<EndClientAreaBackendClientInitialization />
							<Outlet />
						</DefinitionsProvider>
					</AuthZoneI18nProvider>
				</OriginTenantSettingsProvider>
			</AppMetaProvider>
		</AuthenticatedRoutesGuard>
	);
};

const NonAuthZoneGuardAndProviders: React.FC = () => {
	return (
		// As we're in non-auth zone, we can't get AppMeta and therefore and can't know the origin tenant with its settings
		// The only option here is to set defaultLocale="en"
		<I18nProvider defaultLocale="en">
			<Outlet />
		</I18nProvider>
	);
};

export const EndClientAreaRouter: React.FC = () => {
	return (
		<BrowserRouter>
			<Routes>
				<Route element={<NonAuthZoneGuardAndProviders />}>
					<Route path="*" element={<NotFoundRoute />} />
				</Route>

				<Route element={<AuthProvider />}>
					{/* Sign-in route must be available for all users because it handles magic links */}
					<Route element={<NonAuthZoneGuardAndProviders />}>
						<Route path="/sign-in/*" element={<SignInRoute />} />
						<Route path={endClientAreaPaths.signOut()} element={<SignOutRoute />} />
					</Route>

					{/* Authenticated routes */}
					<Route element={<AuthZoneGuardAndProviders />}>
						<Route element={<AppLayout />}>
							<Route path={endClientAreaPaths.startApplication()} element={<MainRoute />} />
							<Route element={<ApplicationLayout />}>
								{/* We don't have a "main" application route, so this route just redirects to the first form */}
								<Route path={endClientAreaPaths.application(':applicationNodeId')} element={<ApplicationRoute />} />
								<Route
									path={endClientAreaPaths.applicationForm(':applicationNodeId', ':formName', ':formHash')}
									element={<ApplicationFormRoute />}
								/>
								<Route
									path={endClientAreaPaths.applicationConnection(':applicationNodeId', ':applicationConnectionKind')}
									element={<ApplicationConnectionRoute />}
								/>
								<Route
									path={endClientAreaPaths.applicationSubmit(':applicationNodeId')}
									element={<ApplicationSubmitRoute />}
								/>
							</Route>
							<Route path={endClientAreaPaths.applicationSubmitted()} element={<SuccessRoute />} />
						</Route>
					</Route>
				</Route>
			</Routes>
		</BrowserRouter>
	);
};
